"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/nextjs";
import { recordPageVisit } from "@/lib/server-actions/badgeActions";

// 학습 페이지에 이 시간(ms) 이상 머문 뒤에만 방문 기록(빠른 이탈은 무시, StudyVisitTracker와 동일 톤).
const DWELL_MS = 3000;

/**
 * 이론/코딩 학습 페이지 방문을 기록해 "과목 마스터" 배지를 수여하는 훅 (JCG-2262).
 *
 * globals 슬롯에 한 번 마운트되는 `PageVisitTracker`가 `usePathname()`으로 현재 경로를 넘긴다.
 * 영구 마운트에서도 안전하도록 boolean 대신 **경로별 dedup**(lastRecorded)을 사용 — 경로가 바뀔 때마다
 * 새로 기록하되 같은 경로 재실행은 무시. 3초 dwell 후 기록한다.
 *
 * 서버 `recordPageVisit`이 과목 콘텐츠 페이지인지 최종 판정하고, 비-과목 페이지(인덱스·공부계획 등)는
 * Firebase 읽기 없이 종료한다. 배지는 조용히 수여되며 사용자는 배지 페이지에서 확인한다.
 */
export function usePageVisit(pagePath: string) {
  const { isLoaded, isSignedIn } = useAuth();
  const lastRecorded = useRef<string | null>(null);

  useEffect(() => {
    // isLoaded 전(isSignedIn===undefined)에 판정해 첫 페이지 누락하는 것 방지(StudyVisitTracker와 동일).
    if (!isLoaded || !isSignedIn) return;
    if (lastRecorded.current === pagePath) return;
    // 이론/코딩 학습 페이지만 (정확한 과목 판정은 서버 recordPageVisit이 수행)
    if (!pagePath.startsWith("/theory/") && !pagePath.startsWith("/coding/"))
      return;

    const timer = setTimeout(() => {
      lastRecorded.current = pagePath;
      recordPageVisit(pagePath).catch((error) => {
        lastRecorded.current = null; // 실패 시 재시도 가능하도록 dedup 해제
        console.error("페이지 방문 기록 오류:", error);
      });
    }, DWELL_MS);

    return () => clearTimeout(timer);
  }, [pagePath, isLoaded, isSignedIn]);
}
